import { useEffect, useState } from 'react';
import { Loader2, Check, Landmark, AlertTriangle, CreditCard } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { ACCENT as SECTION_COLOR, OK, WARN } from '../../lib/uiTokens';

/**
 * Zaplacení dokladu, který se platil přes účet — párování z druhé strany.
 *
 * Doklad převodem, kartou nebo inkasem se má do „zaplacen" dostat jedině
 * spárováním s bankovním pohybem; jinak v P&L visí zaplacená faktura, ke které
 * nikdy nikdo nenajde peníze. Dosud vedla jediná cesta z Banky: projít výpis
 * a k pohybu dohledat doklad. Kdo ale fakturu zaplatil a chce ji zavřít, jde
 * od dokladu — a ten směr chyběl.
 *
 * Návrhy počítá `BankMatcher::navrhyProDoklad` (VS, částka ±1 %, účet, IČO,
 * blízkost splatnosti). Samotné spárování jde přes **týž** endpoint, který
 * používá Banka, takže se logika přechodu do „zaplacen" nerozdvojuje.
 *
 * Hotovost sem nechodí — ta se označí zaplacená jedním klikem.
 */

const fmt = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');

export default function ZaplatitModal({ doklad, isDark, token, onClose, onHotovo }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [vybrany, setVybrany] = useState(null);
    const [paruje, setParuje] = useState(false);
    const [bezParovani, setBezParovani] = useState(false);
    const [duvod, setDuvod] = useState('');

    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const plocha = isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)';

    useEffect(() => {
        let zivy = true;
        fetch(`/api/finance/doklady/${doklad.id}/navrhy-plateb`, { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(d => {
                if (!zivy) return;
                setData(d);
                // Nejlepší návrh předvybereme, ale jen když opravdu sedí — u shody
                // pouhé částky ať člověk vybere sám, těch může být víc stejných.
                const nej = d.navrhy?.[0];
                if (nej && nej.skore >= 5) setVybrany(nej.id);
            })
            .catch(() => zivy && setChyba('Návrhy plateb se nepodařilo načíst.'));
        return () => { zivy = false; };
    }, [doklad.id]);

    const sparuj = async () => {
        if (!vybrany) return;
        setParuje(true); setChyba(null);
        try {
            const r = await fetch(`/api/finance/banka/transakce/${vybrany}/parovat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({
                    alokace: [{ doklad_id: doklad.id, castka: doklad.castka_s_dph }],
                    oznacit_zaplaceno: true,
                }),
            });
            if (r.ok) { onHotovo?.(); onClose?.(); return; }
            const j = await r.json().catch(() => ({}));
            setChyba(j.error || 'Spárování selhalo.');
        } catch { setChyba('Síťová chyba.'); }
        setParuje(false);
    };

    const zaplatitBezParovani = async () => {
        if (!duvod.trim()) { setChyba('Napiš důvod — zapíše se do historie dokladu.'); return; }
        setParuje(true); setChyba(null);
        try {
            const r = await fetch(`/api/finance/doklady/${doklad.id}/zaplatit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ duvod: duvod.trim() }),
            });
            if (r.ok) { onHotovo?.(); onClose?.(); return; }
            const j = await r.json().catch(() => ({}));
            setChyba(j.error || 'Označení zaplaceno selhalo.');
        } catch { setChyba('Síťová chyba.'); }
        setParuje(false);
    };

    const navrhy = data?.navrhy || [];

    return (
        <ModalShell open onClose={onClose} isDark={isDark} sirka={560}
            nadpis="Zaplatit z účtu" ikona={Landmark} barva={SECTION_COLOR}
            paticka={(
                <div className="flex items-center gap-2">
                    <span className="text-[11px] flex-1" style={{ color: 'var(--text-tertiary)' }}>
                        {vybrany ? 'Pohyb se spáruje a doklad přejde na „zaplacen".' : 'Vyber pohyb, kterým byl doklad zaplacen.'}
                    </span>
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 rounded-lg text-xs"
                        style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}`, background: 'transparent' }}>
                        Zavřít
                    </button>
                    <button type="button" onClick={sparuj} disabled={!vybrany || paruje}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ background: SECTION_COLOR, opacity: (!vybrany || paruje) ? 0.45 : 1 }}>
                        {paruje ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        Spárovat a zaplatit
                    </button>
                </div>
            )}>

            <div className="text-[12px] mb-3" style={{ color: 'var(--text-secondary)' }}>
                Hledám nespárované odchozí pohyby k dokladu na{' '}
                <b style={{ color: 'var(--text-primary)' }}>{fmt(doklad.castka_s_dph)} Kč</b>
                {data?.doklad?.variabilni_symbol && <> · VS <b style={{ color: 'var(--text-primary)' }}>{data.doklad.variabilni_symbol}</b></>}
                {data?.doklad?.datum_splatnosti && <> · splatnost {fmtDate(data.doklad.datum_splatnosti)}</>}
            </div>

            {!data && !chyba && (
                <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin" style={{ color: SECTION_COLOR }} />
                </div>
            )}

            {data && navrhy.length === 0 && (
                <div className="rounded-xl p-3 mb-3" style={{ background: `${WARN}14`, border: `1px solid ${WARN}44` }}>
                    <div className="flex items-start gap-2">
                        <AlertTriangle size={15} style={{ color: WARN, flexShrink: 0, marginTop: 1 }} />
                        <div className="text-[12px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            <b style={{ color: WARN }}>Žádný nespárovaný pohyb nesedí.</b><br />
                            {data.vypis_do
                                ? <>Výpis je natažený do <b style={{ color: 'var(--text-primary)' }}>{fmtDate(data.vypis_do)}</b>. Když je platba novější, nejdřív ji natáhni v Bance.</>
                                : <>V bance zatím není žádný pohyb — nahraj výpis.</>}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col" style={{ gap: 6 }}>
                {navrhy.map(t => {
                    const aktivni = vybrany === t.id;
                    return (
                        <button key={t.id} type="button" onClick={() => setVybrany(aktivni ? null : t.id)}
                            className="w-full text-left rounded-xl px-3 py-2"
                            style={{
                                background: aktivni ? `${SECTION_COLOR}14` : plocha,
                                border: `1px solid ${aktivni ? SECTION_COLOR : hranice}`,
                                cursor: 'pointer',
                            }}>
                            <div className="flex items-baseline gap-2">
                                <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-tertiary)', width: 58, flexShrink: 0 }}>
                                    {fmtDate(t.datum)}
                                </span>
                                <span className="flex-1 min-w-0 truncate text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {t.protistrana_nazev || t.popis || 'Bez protistrany'}
                                </span>
                                <span className="text-[13px] font-bold tabular-nums flex-shrink-0" style={{ color: 'var(--text-primary)' }}>
                                    −{fmt(t.castka)}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 3, marginLeft: 66 }}>
                                {t.je_kartova && (
                                    <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                                        <CreditCard size={10} />{t.karta_posledni4 ? `…${t.karta_posledni4}` : 'karta'}
                                    </span>
                                )}
                                {t.variabilni_symbol && (
                                    <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>VS {t.variabilni_symbol}</span>
                                )}
                                {/* Proč to systém navrhl — ať se dá věřit nebo nevěřit. */}
                                {(t.duvody || []).map(d => (
                                    <span key={d} className="rounded-full text-[9.5px] font-semibold"
                                        style={{ padding: '1px 6px', background: `${OK}1f`, color: OK }}>{d}</span>
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>

            {chyba && <div className="text-[12px] mt-3" style={{ color: WARN }}>{chyba}</div>}

            {/* Úniková cesta. Nenápadně dole a s důvodem do historie — doklady
                z minulosti, dobropisy nebo platba ze soukromé karty existují,
                ale nemají se dělat omylem. */}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${hranice}` }}>
                {!bezParovani ? (
                    <button type="button" onClick={() => setBezParovani(true)}
                        className="text-[11px]" style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Označit zaplaceno bez párování…
                    </button>
                ) : (
                    <div className="flex flex-col" style={{ gap: 7 }}>
                        <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                            Doklad zůstane bez vazby na bankovní pohyb. Důvod se zapíše do historie.
                        </span>
                        <div className="flex items-center gap-2">
                            <input type="text" value={duvod} onChange={e => setDuvod(e.target.value)} autoFocus
                                placeholder="Např.: zaplaceno ze soukromé karty, doloženo účtenkou"
                                className="flex-1 min-w-0 rounded-lg px-2.5 py-1.5 text-[12px]"
                                style={{ background: plocha, border: `1px solid ${hranice}`, color: 'var(--text-primary)', outline: 'none' }} />
                            <button type="button" onClick={zaplatitBezParovani} disabled={paruje || !duvod.trim()}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
                                style={{
                                    background: 'transparent', color: WARN, border: `1px solid ${WARN}80`,
                                    opacity: (paruje || !duvod.trim()) ? 0.45 : 1, cursor: duvod.trim() ? 'pointer' : 'not-allowed',
                                }}>
                                Zaplatit bez párování
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </ModalShell>
    );
}
